import { useCallback, useEffect, useState } from 'react'
import { Plus, ShieldPlus } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, ErrorState, Skeleton } from '@/components/States'
import { StatusBadge } from '@/components/StatusBadge'
import { useToast } from '@/providers/UiProviders'
import { ApiError } from '@/lib/api'
import { createFraudRule, fetchRules, FRAUD_STR as S, type FraudRuleRow } from './fraudApi'

type Action = 'flag' | 'block' | 'review'
const ACTIONS: { value: Action; label: string }[] = [
  { value: 'flag', label: S.actionFlag },
  { value: 'block', label: S.actionBlock },
  { value: 'review', label: S.actionReview },
]

/**
 * A25.03 — Создать правило. Form posts to the REAL
 * `POST /admin/antifraud/rules`, then reloads the existing-rules list below.
 * Placeholder permission USERS_VIEW.
 */
export function RuleCreateScreen() {
  const { toast } = useToast()

  const [name, setName] = useState('')
  const [condition, setCondition] = useState('')
  const [action, setAction] = useState<Action>('flag')
  const [busy, setBusy] = useState(false)

  const [rows, setRows] = useState<FraudRuleRow[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchRules({ filters: [], sort: [], page: 0, pageSize: 100, search: '', signal: ac.signal })
      .then((res) => setRows(res.rows))
      .catch((e) => {
        if (!ac.signal.aborted)
          setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const submit = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createFraudRule(name.trim(), condition.trim(), action)
      setName('')
      setCondition('')
      setAction('flag')
      toast(S.ruleCreated, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.createRule} screenId="A25.03" description={S.ruleCreateDesc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="space-y-2 rounded-lg border border-accent bg-surface-2 p-3">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={S.namePh}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
            />
            <input
              value={condition}
              onChange={(e) => setCondition(e.target.value)}
              placeholder={S.conditionPh}
              className="w-full rounded-md border border-border-default bg-surface-1 px-3 py-2 font-mono text-sm text-primary outline-none focus:border-accent"
            />
            <div className="flex items-center gap-2">
              <select
                value={action}
                onChange={(e) => setAction(e.target.value as Action)}
                className="rounded-md border border-border-default bg-surface-1 px-2 py-2 text-sm text-primary"
              >
                {ACTIONS.map((a) => (
                  <option key={a.value} value={a.value}>
                    {a.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={submit}
                disabled={busy || !name.trim()}
                className="flex items-center gap-1 rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
              >
                <Plus size={14} />
                {S.create}
              </button>
            </div>
          </div>

          <div className="text-xs font-medium uppercase tracking-wide text-tertiary">
            {S.existingRules}
          </div>
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <EmptyState title={S.empty} icon={<ShieldPlus size={28} strokeWidth={1.5} />} />
          ) : (
            <div className="space-y-2">
              {rows.map((r) => (
                <div
                  key={r.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-2 p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-primary">{r.name}</span>
                      <StatusBadge status={r.enabled ? 'active' : 'suspended'} />
                    </div>
                    {r.condition && (
                      <p className="mt-0.5 truncate font-mono text-xs text-tertiary">{r.condition}</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-3 text-xs text-tertiary">
                    <span className="rounded bg-surface-3 px-1.5 py-0.5">{r.action}</span>
                    <span>
                      {r.hits} {S.hits}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
